import { createContext, forwardRef, useContext } from "react"

import * as TabsPrimitive from "@radix-ui/react-tabs"
import { match } from "ts-pattern"

import { cn } from "@/lib/utils"

interface TabsContext {
  variant: "default" | "tag"
}

const TabsContext = createContext<TabsContext>({
  variant: "default",
})

export const Root = forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentProps<typeof TabsPrimitive.Root> & { variant?: TabsContext["variant"] }
>(function Root({ variant = "default", ...props }, ref) {
  return (
    <TabsContext.Provider value={{ variant }}>
      <TabsPrimitive.Root ref={ref} {...props} />
    </TabsContext.Provider>
  )
})

export const Content = forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentProps<typeof TabsPrimitive.Content>
>(function Content({ className, ...props }, ref) {
  return (
    <TabsPrimitive.Content ref={ref} className={cn("min-h-0", className)} {...props} />
  )
})

export const List = forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentProps<typeof TabsPrimitive.List>
>(function List({ className, ...props }, ref) {
  const { variant } = useContext(TabsContext)

  const listClasses = match(variant)
    .with(
      "default",
      () => /*tw*/ "gap mx-2 mb-3 flex gap-10 border-b border-solid border-grey-300",
    )
    .with("tag", () => /*tw*/ "gap flex gap-6")
    .exhaustive()

  return (
    <TabsPrimitive.List ref={ref} className={cn(listClasses, className)} {...props} />
  )
})

export const Trigger = forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentProps<typeof TabsPrimitive.Trigger> & { disabled?: boolean }
>(function Trigger({ children, className, disabled, ...props }, ref) {
  const { variant } = useContext(TabsContext)

  const triggerClasses = match(variant)
    .with(
      "default",
      () =>
        /*tw*/ `${
          disabled
            ? "cursor-default text-grey-400 border-transparent"
            : "flex cursor-pointer items-center gap-2 border-b border-solid border-transparent pb-2 text-grey-700 hover:border-grey-500 hover:text-grey-900 aria-selected:border-black aria-selected:text-grey-900"
        }`,
    )
    .with(
      "tag",
      () =>
        /*tw*/ `${
          disabled
            ? "cursor-default text-grey-400"
            : "flex cursor-pointer items-center gap-2 rounded-md p-2 px-3 text-grey-700 aria-selected:bg-brand-primary-700 aria-selected:text-white transition-all  hover:bg-grey-200"
        }`,
    )
    .exhaustive()

  return (
    <TabsPrimitive.Trigger
      className={cn("text-body1 whitespace-nowrap", triggerClasses, className)}
      {...props}
      ref={ref}
      disabled={disabled}
      aria-disabled={disabled}
    >
      {children}
    </TabsPrimitive.Trigger>
  )
})
